import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import { PhotographyApiService } from './api.service';
import { PhotographyListResponse } from '../models/photography-vendor.model';
import {
  PhotographyBundle, PhotographyBundleInclude, PhotographyBundleOption, PhotographyBundlePrice,
} from '../models/photography-catalog.model';

const BUNDLE_PRICES = 'bundle-prices';

/** Mirrors CreateBundleRequest in @lcms-photography/core. Updates send any subset of these. */
export interface PhotographyBundleRequest {
  vendorId?: string;
  name?: string;
  description?: string;
  packageType?: string;
  maxOptionCount?: number;
}

/** Mirrors CreateBundlePriceRequest in @lcms-photography/core. Money values are decimal strings like "150.00". */
export interface PhotographyBundlePriceRequest {
  bundleId?: string;
  vendorRegionId?: string;
  releaseId?: string;
  cost?: string;
  travelCost?: string;
}

/** Bundles in the lcms-photography catalog, with their included products, optional products and prices */
@Injectable({
  providedIn: 'root',
})
export class PhotographyBundleService {
  private api = inject(PhotographyApiService);

  readonly resource = 'bundles';

  get$(bundleId: string): Observable<PhotographyBundle> {
    return this.api.get<PhotographyBundle>(`${this.resource}/${bundleId}`);
  }

  create(bundle: PhotographyBundleRequest): Promise<PhotographyBundle> {
    return this.api.create<PhotographyBundle, PhotographyBundleRequest>(this.resource, bundle);
  }

  update(bundleId: string, bundle: PhotographyBundleRequest): Promise<PhotographyBundle> {
    return this.api.update<PhotographyBundle, PhotographyBundleRequest>(`${this.resource}/${bundleId}`, bundle);
  }

  /** Rejects with a 409 while prices, includes or options still reference the bundle */
  delete(bundleId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(`${this.resource}/${bundleId}`));
  }

  includes$(bundleId: string): Observable<PhotographyBundleInclude[]> {
    return this.api.get<{ items: PhotographyBundleInclude[] }>(`${this.resource}/${bundleId}/includes`).pipe(
      map((response) => response.items),
    );
  }

  /** Idempotent: including a product twice is a no-op */
  addInclude(bundleId: string, productId: string): Promise<void> {
    return firstValueFrom(this.api.put<void>(`${this.resource}/${bundleId}/includes/${productId}`));
  }

  removeInclude(bundleId: string, productId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(`${this.resource}/${bundleId}/includes/${productId}`));
  }

  options$(bundleId: string): Observable<PhotographyBundleOption[]> {
    return this.api.get<{ items: PhotographyBundleOption[] }>(`${this.resource}/${bundleId}/options`).pipe(
      map((response) => response.items),
    );
  }

  /** Not idempotent: adding a product that is already an option breaks a unique constraint */
  addOption(bundleId: string, productId: string): Promise<PhotographyBundleOption> {
    const route = `${this.resource}/${bundleId}/options`;
    return this.api.create<PhotographyBundleOption, { productId: string }>(route, { productId });
  }

  removeOption(bundleId: string, bundleOptionId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(`${this.resource}/${bundleId}/options/${bundleOptionId}`));
  }

  prices$(bundleId: string): Observable<PhotographyBundlePrice[]> {
    return this.api.get<PhotographyListResponse<PhotographyBundlePrice>>(BUNDLE_PRICES, { bundleId, limit: 200 }).pipe(
      map((response) => response.items),
    );
  }

  getPrice$(bundlePriceId: string): Observable<PhotographyBundlePrice> {
    return this.api.get<PhotographyBundlePrice>(`${BUNDLE_PRICES}/${bundlePriceId}`);
  }

  createPrice(price: PhotographyBundlePriceRequest): Promise<PhotographyBundlePrice> {
    return this.api.create<PhotographyBundlePrice, PhotographyBundlePriceRequest>(BUNDLE_PRICES, price);
  }

  updatePrice(bundlePriceId: string, price: PhotographyBundlePriceRequest): Promise<PhotographyBundlePrice> {
    const route = `${BUNDLE_PRICES}/${bundlePriceId}`;
    return this.api.update<PhotographyBundlePrice, PhotographyBundlePriceRequest>(route, price);
  }

  deletePrice(bundlePriceId: string): Promise<void> {
    return firstValueFrom(this.api.delete<void>(`${BUNDLE_PRICES}/${bundlePriceId}`));
  }
}
